import '../css/GroupPage.less';

import {ApolloClient} from 'apollo-client';
import React, {useEffect, useMemo} from 'react';

import FancyPage from '../components/FancyPage';
import MultiRunWorkspace from '../components/MultiRunWorkspace';
import NoMatch from '../components/NoMatch';
import WandbLoader from '../components/WandbLoader';
import {useGroupPageQuery} from '../state/graphql/groupPageQuery';
import {useDispatch} from '../state/hooks';
import * as PollingActions from '../state/polling/actions';
import * as Filter from '../util/filters';
import {AnalyticsEvents} from '../util/integrations';
import {setDocumentTitle, setDocumentDescription} from '../util/document';
import makeComp from '../util/profiler';

const POLL_INTERVAL = 15000;

interface GroupPageProps {
  client: ApolloClient<any>;
  history: any;
  match: {
    params: {
      entityName: string;
      projectName: string;
      projectId: string;
      groupName: string;
      tab: string;
    };
  };
}

export default makeComp(
  (props: GroupPageProps) => {
    const {entityName, projectName, groupName, tab} = props.match.params;

    const groupQuery = useGroupPageQuery({
      entityName,
      projectName,
    });

    const dispatch = useDispatch();

    useEffect(() => {
      window.analytics.track(AnalyticsEvents.MULTI_RUN_WORKSPACE, {
        page: 'Group',
        panelbank: true,
      });
      dispatch(PollingActions.setBasePollInterval(POLL_INTERVAL));
    }, [dispatch, groupName, entityName, projectName]);

    const project = !groupQuery.loading ? groupQuery.project : null;

    const tabDescription: string = useMemo(() => {
      switch (tab) {
        case `table`:
          return `Table`;
        case `workspace`:
        default:
          return 'Workspace';
      }
    }, [tab]);

    useEffect(() => {
      setDocumentTitle(`${groupName} | ${projectName} ${tabDescription}`);
      setDocumentDescription(
        `${tabDescription} of group ${groupName} in ${projectName}, a machine learning project by ${entityName} using Weights & Biases.`
      );
    }, [tabDescription, groupName, projectName, entityName]);

    if (groupQuery.loading) {
      return <WandbLoader />;
    }
    if (!project) {
      return <NoMatch />;
    }

    const mergeFilters: Filter.Filter = {
      key: {section: 'run', name: 'group'},
      op: '=',
      value: groupName === '<null>' ? null : groupName,
    };

    const baseUrl =
      '/' + entityName + '/' + projectName + '/groups/' + groupName;

    const onSetTableExpanded = (expanded: boolean) => {
      props.history.push({
        pathname: baseUrl + (expanded ? '/table' : '/workspace'),
        search: window.location.search,
      });
    };

    const workspaceTabBody = (tableExpanded: boolean) => () => (
      <MultiRunWorkspace
        entityName={entityName}
        projectName={projectName}
        tableExpanded={tableExpanded}
        onSetTableExpanded={onSetTableExpanded}
        viewType="group-view"
        title={`Group: ${groupName}`}
        mergeFilters={mergeFilters}
        recommendGrouping="jobtype"
      />
    );

    return (
      <div className="group-page">
        <FancyPage
          history={props.history}
          activeSlug={tab}
          baseUrl={baseUrl}
          defaultIndex={0}
          items={[
            {
              iconName: 'cecile-workspace-gray',
              selectedIconName: 'cecile-workspace',
              name: 'Workspace',
              slug: 'workspace',
              render: workspaceTabBody(false),
            },
            {
              iconName: 'cecile-table-gray',
              selectedIconName: 'cecile-table',
              name: 'Table',
              slug: 'table',
              render: workspaceTabBody(true),
            },
          ]}
        />
      </div>
    );
  },
  {id: 'GroupPage'}
);
